import { clearAccessToken, getAccessToken } from '@/lib/auth';

const baseUrl = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, '') ?? '';

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function parseJson<T>(res: Response): Promise<T> {
  const text = await res.text();
  if (!text) return undefined as T;
  return JSON.parse(text) as T;
}

function authHeaders(extra?: HeadersInit): Headers {
  const headers = new Headers(extra);
  const token = getAccessToken();
  if (token) headers.set('Authorization', `Bearer ${token}`);
  return headers;
}

export async function apiFetch<T>(
  path: string,
  init?: RequestInit & { json?: unknown },
): Promise<T> {
  const headers = authHeaders(init?.headers);
  if (init?.json !== undefined) {
    headers.set('Content-Type', 'application/json');
  }

  const url = path.startsWith('http')
    ? path
    : `${baseUrl}${path.startsWith('/api/') ? path : `/api/v1${path.startsWith('/') ? path : `/${path}`}`}`;

  const res = await fetch(url, {
    ...init,
    headers,
    credentials: 'include',
    body: init?.json !== undefined ? JSON.stringify(init.json) : init?.body,
  });

  if (!res.ok) {
    if (res.status === 401 && getAccessToken()) {
      clearAccessToken();
    }
    const body = await parseJson<unknown>(res).catch(() => undefined);
    const message =
      body &&
      typeof body === 'object' &&
      'error' in body &&
      typeof (body as { error: unknown }).error === 'string'
        ? (body as { error: string }).error
        : res.statusText || 'Request failed';
    throw new ApiError(message, res.status, body);
  }

  return parseJson<T>(res);
}

export const api = {
  get: <T>(path: string) => apiFetch<T>(path),
  post: <T>(path: string, json?: unknown) => apiFetch<T>(path, { method: 'POST', json }),
  put: <T>(path: string, json?: unknown) => apiFetch<T>(path, { method: 'PUT', json }),
  patch: <T>(path: string, json?: unknown) => apiFetch<T>(path, { method: 'PATCH', json }),
  delete: <T>(path: string) => apiFetch<T>(path, { method: 'DELETE' }),
  health: () => apiFetch<{ ok: boolean }>('/api/v1/health'),
  products: (params?: { network?: string; page?: number }) => {
    const qs = new URLSearchParams();
    if (params?.network) qs.set('network', params.network);
    if (params?.page) qs.set('page', String(params.page));
    const q = qs.toString();
    return apiFetch<{
      items: import('@dzseguidores/types').ProductPublic[];
      pagination: { page: number; limit: number; total: number; pages: number };
    }>(`/api/v1/catalog/products${q ? `?${q}` : ''}`);
  },
  product: (slug: string) =>
    apiFetch<import('@dzseguidores/types').ProductPublic>(`/api/v1/catalog/products/${slug}`),
  login: (body: import('@dzseguidores/types').LoginInput) =>
    apiFetch<{ accessToken: string; roles: string[]; refreshToken?: string }>(
      '/api/v1/auth/login',
      { method: 'POST', json: body },
    ),
  registerRequestOtp: (body: import('@dzseguidores/types').RegisterInput) =>
    apiFetch<{
      sent: boolean;
      email: string;
      expiresInSeconds: number;
      devCode?: string;
    }>('/api/v1/auth/register/request-otp', {
      method: 'POST',
      json: body,
    }),
  registerResendOtp: (body: import('@dzseguidores/types').ResendRegisterOtpInput) =>
    apiFetch<{
      sent: boolean;
      email: string;
      expiresInSeconds: number;
      devCode?: string;
    }>('/api/v1/auth/register/resend-otp', {
      method: 'POST',
      json: body,
    }),
  registerConfirm: (body: import('@dzseguidores/types').ConfirmRegisterInput) =>
    apiFetch<{
      accessToken: string;
      refreshToken?: string;
      roles: string[];
      user: { id: string; email: string; name: string };
    }>('/api/v1/auth/register/confirm', {
      method: 'POST',
      json: body,
    }),
  /** @deprecated Prefer registerRequestOtp — starts OTP, does not return accessToken. */
  register: (body: import('@dzseguidores/types').RegisterInput) =>
    apiFetch<{
      sent?: boolean;
      email?: string;
      expiresInSeconds?: number;
      devCode?: string;
      accessToken?: string;
      user?: { id: string };
    }>('/api/v1/auth/register', {
      method: 'POST',
      json: body,
    }),
  placeOrder: (body: import('@dzseguidores/types').PlaceOrderInput, idempotencyKey: string) =>
    apiFetch<{
      order: {
        id: string;
        number: string;
        status: string;
        totalCents: string;
      };
      payment: {
        id: string;
        method: 'PIX' | 'CARD';
        status: string;
        pixQrCode?: string | null;
        pixCopyPaste?: string | null;
        expiresAt?: string | null;
      };
    }>('/api/v1/checkout/orders', {
      method: 'POST',
      json: body,
      headers: { 'Idempotency-Key': idempotencyKey },
    }),
  paymentConfig: () =>
    apiFetch<{
      sandbox: boolean;
      mode: 'sandbox' | 'production' | 'simulated';
      pixEnabled: boolean;
      cardEnabled: boolean;
      provider: 'asaas';
    }>('/api/v1/checkout/payment-config'),
};
